import { useEffect, useState } from "react";
import useForm from "antd/es/form/hooks/useForm";
import type { RangePickerProps } from "antd/es/date-picker";
import { useAuthManageStore } from "@/pages/authorization-management/store/auth-manage-store";
import { CheckboxOptionType } from "antd/lib/checkbox";
import { useGlobalStore } from "@/store/GlobalStore";
import { useSubAccountStore } from "@/store/SubAccountStore";
import { usePermissionStore } from "@/store/PermissionStore";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { useWatch } from "antd/es/form/Form";
import {
  AuthTime,
  PlatformCodeMap,
} from "@/pages/authorization-management/components/auth-edit-drawer/constants";
import request from "project-config/services/request/request";
import { getPermissionIds } from "@/pages/authorization-management/components/auth-edit-drawer/helper/getPermissionIds";
import { message } from "antd";
import { ValidatorRule } from "rc-field-form/lib/interface";
import { AuthStatus } from "@/pages/authorization-management/constants/authStatus";
import { dateFormatter } from "project-config/utils/date-formatter";
import dayjs from "project-config/utils/dayjs";
import type { Dayjs } from "dayjs";

export interface IAuthFormData {
  authType: number;
  expireDate: typeof dayjs;
  checkedAccount: number[];
  checkedPlatform: number[];
  startDate: string;
}

export function useAuthEdit() {
  const [showExpireDatePicker, setShowExpireDatePicker] = useState(false);
  const {
    authRecord,
    setOpenEditAuthDrawer,
    authObjectPermissionMap,
    fetchUserAuthRecord,
    _setAuthObjectPermissionMap,
    authStatus,
    setAuthStatus,
    checkAllPermission,
    setCheckAllPermission,
  } = useAuthManageStore();
  const { userInfo, isDomestic } = useGlobalStore();
  const { subAccountList } = useSubAccountStore();
  const { allPermission } = usePermissionStore();
  const [form] = useForm<IAuthFormData>();
  const t = getTranslateNsT("center");
  const checkedAccount = useWatch("checkedAccount", form);
  const checkedPlatform = useWatch("checkedPlatform", form);

  const isSubmitDisabled =
    Object.values(authObjectPermissionMap).flat().length === 0 ||
    !checkedAccount?.length ||
    !checkedPlatform?.length;

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    return (
      current &&
      (current < dayjs().startOf("day") ||
        current > dayjs().add(8, "days").startOf("day"))
    );
  };
  const range = (start: number, end: number) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };
  const disabledDateTime = (now: any) => {
    const date = dayjs(now);
    const hours = dayjs().hour();
    const minutes = dayjs().minute();
    const seconds = dayjs().second();
    // 当天
    if (
      +date < +dayjs().add(1, "days") &&
      +date.day() === +dayjs().format("d")
    ) {
      let disableSeconds: number[] = [];
      if (+date.minute() === +dayjs().minute()) {
        disableSeconds = range(0, 60).splice(0, minutes);
      }
      return {
        disabledHours: () => range(0, 24).splice(0, hours),
        disabledMinutes: () => range(0, 60).splice(0, minutes),
        disabledSeconds: () => disableSeconds,
      };
    } else if (+date.day() === +dayjs().format("d")) {
      // 最后一天
      return {
        disabledHours: () => range(0, 24).splice(hours + 1),
        disabledMinutes: () => range(0, 60).splice(minutes + 1),
        disabledSeconds: () => range(0, 60).splice(seconds + 1),
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };
  const onDateChange = (date: Dayjs | null, str: string) => {
    if (!date) return;
    const maxDate = dayjs().add(7, "days");
    if (+date > +maxDate) {
      form.setFieldValue("expireDate", maxDate);
    }
  };
  const checkAccountOptions: CheckboxOptionType[] = subAccountList.reduce(
    (pre, cur) => {
      cur.subUserName &&
        cur.id &&
        pre.push({
          label: cur.subUserName as string,
          value: cur.id as number,
        });
      return pre;
    },
    [
      {
        label: `${userInfo?.userName}（${t("user_current_account")}）`,
        value: userInfo?.userId as number,
      },
    ]
  );
  const checkPlatformOptions: CheckboxOptionType[] = Object.keys(
    PlatformCodeMap
  )
    .filter((key) => (isDomestic ? true : key !== "RenderingClient"))
    .map((_key) => ({
      label: t(`user_auth_form_${_key}`),
      value: PlatformCodeMap[_key as keyof typeof PlatformCodeMap],
    }));
  const authTimeOptions = Object.keys(AuthTime).map((key) => ({
    label: t(`user_auth_form_${key}`),
    value: AuthTime[key as keyof typeof AuthTime],
  }));
  const validateExpireDate: ValidatorRule["validator"] = (_, value) => {
    if (showExpireDatePicker && +value < +new Date()) {
      return Promise.reject(t("user_auth_date_tip"));
    }
    return Promise.resolve();
  };
  const validateCheckedAccount: ValidatorRule["validator"] = (_, value) => {
    if (!value || !value.length) {
      return Promise.reject(t("user_auth_rule_3"));
    }
    return Promise.resolve();
  };
  const validateCheckedPlatform: ValidatorRule["validator"] = (_, value) => {
    if (!value || !value.length) {
      return Promise.reject(t("user_auth_rule_4"));
    }
    return Promise.resolve();
  };
  function handleCancel() {
    setOpenEditAuthDrawer(false);
    if (!authRecord) return;
    _setAuthObjectPermissionMap(authRecord?.permissionBos);
  }
  async function handleSubmitAuth() {
    function changeAuthStatus() {
      if (authStatus !== AuthStatus.Effective) {
        setAuthStatus(AuthStatus.Effective);
      }
    }
    try {
      await form.validateFields();
    } catch (e: any) {
      form.scrollToField(e.errorFields[0].name[0]);
      return;
    }
    const authObjectIds = Object.keys(authObjectPermissionMap)
      .filter((key) => authObjectPermissionMap[key].length > 0)
      .map((id) => +id);
    const userPermissionIds = [
      ...allPermission.map((module) => module.id),
      ...authObjectIds,
      ...Object.values(authObjectPermissionMap).flat(),
    ];
    const authType = form.getFieldValue("authType");
    const params = {
      endDate:
        authType === AuthTime.Custom
          ? +form.getFieldValue("expireDate")
          : +new Date() + authType * 24 * 60 * 60 * 1000,
      userIds: form.getFieldValue("checkedAccount"),
      platforms: form.getFieldValue("checkedPlatform"),
      userPermissionIds,
    };
    if (!authRecord || authRecord.cancelFlag) {
      await request.post(
        "/api/rendering/user/userAuth/addUserAuthRecord",
        params
      );
      fetchUserAuthRecord();
      setOpenEditAuthDrawer(false);
      changeAuthStatus();
      return;
    }
    const isCheckedAccountChange =
      `${params.userIds.sort()}` !==
      `${authRecord.authAccounts.map((item) => item.userId).sort()}`;
    const isCheckedPlatformChange =
      `${params.platforms.sort()}` !== `${authRecord.platforms.sort()}`;
    const lastPermissionIds = authRecord.permissionBos
      ? getPermissionIds(authRecord.permissionBos)
      : [];
    const isPermissionChange =
      `${userPermissionIds.sort()}` !== `${lastPermissionIds.sort()}`;
    if (
      !isCheckedAccountChange &&
      !isCheckedPlatformChange &&
      !isPermissionChange
    ) {
      const res = await request.post(
        "/api/rendering/user/userAuth/updateUserAuthRecord",
        {
          id: authRecord.id,
          endDate: params.endDate,
        }
      );
      if (res.code === 200) {
        message.success(t("user_auth_success"));
        await fetchUserAuthRecord();
      }
      changeAuthStatus();
      setOpenEditAuthDrawer(false);
      return;
    }
    const res = await request.post(
      "/api/rendering/user/userAuth/addUserAuthRecord",
      params
    );
    if (res.code === 200) {
      message.success(t("user_auth_success"));
      await fetchUserAuthRecord();
    }
    changeAuthStatus();
    setOpenEditAuthDrawer(false);
  }
  function handleCheckAll() {
    if (checkAllPermission) {
      _setAuthObjectPermissionMap([]);
    } else {
      _setAuthObjectPermissionMap(allPermission);
    }
    setCheckAllPermission(!checkAllPermission);
  }

  useEffect(() => {
    form.setFieldsValue({
      authType: authRecord ? AuthTime.Custom : AuthTime.SevenDay,
      expireDate: authRecord ? dayjs(authRecord.endDate) : dayjs(new Date()),
      startDate: authRecord
        ? dateFormatter(authRecord.startDate)
        : dateFormatter(new Date().getTime()),
      checkedAccount: authRecord
        ? [...authRecord?.authAccounts.map((item) => item.userId as number)]
        : userInfo?.userId
        ? [userInfo?.userId]
        : [],
      checkedPlatform: authRecord
        ? [...authRecord?.platforms]
        : [PlatformCodeMap.WebSide],
    });
    setShowExpireDatePicker(!!authRecord);
  }, [authRecord, form, userInfo]);
  return {
    showExpireDatePicker,
    setShowExpireDatePicker,
    form,
    disabledDate,
    disabledDateTime,
    checkAccountOptions,
    checkPlatformOptions,
    authTimeOptions,
    handleSubmitAuth,
    validateExpireDate,
    validateCheckedAccount,
    validateCheckedPlatform,
    handleCheckAll,
    handleCancel,
    onDateChange,
    isSubmitDisabled,
  };
}
